import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle2, Clock, Box, Eye } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1628900941064-ba8df8b51e4a?w=1200&q=85';

export default function Plans3DPage() {
  const navigate = useNavigate();

  const included = [
    'Relevés précis sur place',
    'Modélisation 3D complète',
    'Rendu photo-réaliste',
    '2-3 propositions de design',
    'Vues multiples (plusieurs angles)',
    'Modifications incluses (2 itérations)'
  ];

  const useCases = [
    {
      title: 'Gros projets',
      desc: 'Rénovation complète : visualiser l\'ensemble avant exécution'
    },
    {
      title: 'Choix matériaux',
      desc: 'Comparer plusieurs options (couleurs, revêtements, agencement)'
    },
    {
      title: 'Validation budget',
      desc: 'Valider les choix esthétiques avant de chiffrer précisément'
    },
    {
      title: 'Aide à la décision',
      desc: 'Se projeter concrètement pour éviter les regrets'
    }
  ];

  const process = [
    { title: 'Prise de mesures', desc: 'Visite sur place, relevés précis', duration: '1-2 jours' },
    { title: 'Modélisation', desc: 'Création du modèle 3D, proposition initiale', duration: '5-7 jours' },
    { title: 'Présentation', desc: 'Rendus photo-réalistes, explication', duration: '1 jour' },
    { title: 'Ajustements', desc: 'Modifications selon vos retours (2 itérations)', duration: '2-3 jours' },
    { title: 'Validation', desc: 'Version finale, base pour devis détaillé', duration: '1 jour' }
  ];

  const faqs = [
    { q: 'Les plans 3D sont-ils inclus dans le devis ?', a: 'Non, c\'est un service optionnel. Il sert d\'outil de validation avant chiffrage détaillé. Utile pour les gros projets.' },
    { q: 'À quoi ça sert concrètement ?', a: 'Visualiser le résultat final, comparer plusieurs options, valider des choix avant exécution. Évite les regrets.' },
    { q: 'Combien de propositions recevrai-je ?', a: '2-3 propositions initiales, puis 2 itérations de modifications incluses.' },
    { q: 'Peut-on modifier après validation ?', a: 'Oui, mais des modifications importantes après validation peuvent impacter le devis final. D\'où l\'intérêt de bien valider avant.' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
              >
                Service optionnel
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-tight"
                style={{ color: 'var(--hr-text)' }}
              >
                Plans 3D & projections photo-réalistes
              </h1>
              
              <p className="text-xl leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                Visualisez votre projet final avant exécution. Comparez des options. Validez vos choix sereinement.
              </p>

              <div className="flex gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                >
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Box className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Photo-réaliste</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>2-3 propositions</span>
                </div>
              </div>
            </div>

            <div className="media-frame">
              <img src={HERO_IMAGE} alt="Plans 3D" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Pourquoi visualiser avant d'exécuter ?
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>
              Parce qu'une fois les travaux lancés, revenir en arrière coûte cher.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Éviter les regrets ("j\'aurais dû choisir une autre couleur")',
              'Comparer plusieurs options sans engagement',
              'Valider l\'agencement avant fabrication',
              'Se projeter concrètement dans le résultat final'
            ].map((benefit, i) => (
              <Card key={benefit} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{benefit}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CAS D'USAGE */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Quand c'est vraiment utile
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((useCase, i) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-8 space-y-3">
                    <h3 className="font-['Figtree'] text-xl font-semibold" style={{ color: 'var(--hr-text)' }}>
                      {useCase.title}
                    </h3>
                    <p className="text-base leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                      {useCase.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI EST INCLUS */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Ce qui est inclus
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {included.map((item) => (
              <Card key={item} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                  <span className="text-base font-medium" style={{ color: 'var(--hr-text)' }}>{item}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Comment ça se déroule
            </h2>
          </div>
          <div className="space-y-4">
            {process.map((step, i) => (
              <Card key={step.title} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-center gap-6">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                    <span className="text-white font-bold text-lg">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Figtree'] text-lg font-semibold mb-1" style={{ color: 'var(--hr-text)' }}>{step.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>{step.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium" style={{ color: 'var(--hr-accent)' }}>{step.duration}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Questions fréquentes
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={faq.q} 
                value={`item-${i}`}
                className="rounded-2xl border px-6" 
                style={{ borderColor: 'var(--hr-border)', backgroundColor: 'var(--hr-bg)' }}
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-['Figtree'] font-semibold" style={{ color: 'var(--hr-text)' }}>{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed pt-2" style={{ color: 'var(--hr-text-muted)' }}>{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-primary-fg)' }}>
            Besoin de visualiser avant d'engager ?
          </h2>
          <p className="text-lg" style={{ color: 'rgba(245,243,239,0.9)' }}>
            Demandez un devis pour votre projet. Nous vous expliquerons si les plans 3D sont pertinents.
          </p>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
          >
            Demander un devis
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}